import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  // Ação primária: Verde original da marca com texto branco
  primary:
    'bg-brand-500 hover:bg-brand-600 text-white shadow-xs border border-transparent active:bg-brand-700',
  // Ação secundária: Neutro com borda fina (superfície do WhatsApp Web)
  secondary:
    'bg-surface-panel-light dark:bg-surface-panel-dark text-content-main-light dark:text-content-main-dark border border-surface-border-light dark:border-surface-border-dark hover:bg-surface-subtle-light dark:hover:bg-surface-subtle-dark',
  // Destrutivo no padrão WhatsApp: Fundo de botão normal (secundário), texto vermelho, hover/active com tom suave de vermelho
  danger:
    'bg-surface-panel-light dark:bg-surface-panel-dark text-red-600 dark:text-red-400 border border-surface-border-light dark:border-surface-border-dark hover:bg-red-50 dark:hover:bg-red-500/10 active:bg-red-100 dark:active:bg-red-500/20 shadow-xs',
  // Ghost: Sem fundo nem borda
  ghost:
    'bg-transparent text-content-muted-light dark:text-content-muted-dark hover:text-content-main-light dark:hover:text-content-main-dark hover:bg-surface-subtle-light dark:hover:bg-surface-subtle-dark',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs gap-1.5 rounded-lg',
  md: 'px-4 py-2 text-xs font-semibold gap-2 rounded-lg',
  lg: 'px-5 py-2.5 text-sm font-semibold gap-2.5 rounded-xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`inline-flex cursor-pointer items-center justify-center whitespace-nowrap font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${variantStyles[variant]} ${sizeStyles[size]} ${className} `}
        {...props}
      >
        {isLoading ? (
          <Spinner
            size="xs"
            color={
              variant === 'secondary' || variant === 'ghost' ? 'brand' : 'white'
            }
          />
        ) : (
          leftIcon
        )}
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {children}
        </span>
        {!isLoading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
